import {
  Tree,
  TreeItem,
  TreeItemLayout,
  Text,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import type { MenuItem } from '../api/menuClient';
import { menuIcon } from './menuIcons';

const useStyles = makeStyles({
  sidebar: {
    width: '260px',
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalS}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    boxSizing: 'border-box',
  },
  brand: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    display: 'flex',
    flexDirection: 'column',
  },
  empty: {
    padding: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
  },
});

interface SidebarProps {
  menu: MenuItem[];
  activeUrl: string;
  onNavigate: (url: string) => void;
}

/**
 * Permission-driven left navigation, rendered from the identity.Menu parent-child tree using the
 * Fluent UI Tree. Leaf nodes (with a Url) navigate; grouping parents expand to reveal their children.
 */
export function Sidebar({ menu, activeUrl, onNavigate }: SidebarProps) {
  const styles = useStyles();

  return (
    <nav className={styles.sidebar} aria-label="Admin navigation">
      <div className={styles.brand}>
        <Text weight="semibold" size={400}>Admin Portal</Text>
      </div>
      {menu.length === 0 ? (
        <Text className={styles.empty} size={200}>No sections available.</Text>
      ) : (
        <Tree aria-label="Navigation menu" defaultOpenItems={menu.map((m) => `menu-${m.id}`)}>
          {menu.map((item) => renderNode(item, activeUrl, onNavigate))}
        </Tree>
      )}
    </nav>
  );
}

function renderNode(
  item: MenuItem,
  activeUrl: string,
  onNavigate: (url: string) => void,
): React.ReactElement {
  const hasChildren = item.children && item.children.length > 0;
  const isActive = !!item.url && item.url === activeUrl;

  const handleClick = () => {
    if (item.url) onNavigate(item.url);
  };

  if (hasChildren) {
    return (
      <TreeItem key={item.id} itemType="branch" value={`menu-${item.id}`}>
        <TreeItemLayout iconBefore={menuIcon(item)}>{item.title}</TreeItemLayout>
        <Tree>
          {item.children.map((child) => renderNode(child, activeUrl, onNavigate))}
        </Tree>
      </TreeItem>
    );
  }

  return (
    <TreeItem key={item.id} itemType="leaf" value={`menu-${item.id}`}>
      <TreeItemLayout
        iconBefore={menuIcon(item)}
        onClick={handleClick}
        style={{
          cursor: item.url ? 'pointer' : 'default',
          fontWeight: isActive ? tokens.fontWeightSemibold : undefined,
          color: isActive ? tokens.colorBrandForeground1 : undefined,
        }}
      >
        {item.title}
      </TreeItemLayout>
    </TreeItem>
  );
}
